import { createAsyncThunk } from "@reduxjs/toolkit";
import { isAxiosError } from "axios";
import { displayAlert } from "../slice/alertSlice.tsx";
import personaGroupsApi from "../api/personaGroupsApi.tsx";
import { AssignPersonasBodyType } from "../types/personaGroupsTypes.tsx";

export const fetchPersonaGroups = createAsyncThunk(
  "personaGroups/fetchPersonaGroups",
  async (_, thunkAPI) => {
    try {
      const response = await personaGroupsApi.fetchPersonaGroups();
      return response.data;
    } catch (e: unknown) {
      if (isAxiosError(e) && e.response?.status === 429) throw e;
      const error = e as { response?: { data?: { detail?: string } } };
      if (error.response?.data) {
        thunkAPI.dispatch(
          displayAlert({
            type: "error",
            text:
              error.response.data.detail || "Failed to fetch persona groups",
          }),
        );
      }
      throw e;
    }
  },
);

export const createPersonaGroup = createAsyncThunk(
  "personaGroups/createPersonaGroup",
  async (
    body: {
      name: string;
      description?: string;
      is_platform?: boolean;
      type?: string;
    },
    thunkAPI,
  ) => {
    try {
      const response = await personaGroupsApi.createPersonaGroup(body);
      thunkAPI.dispatch(
        displayAlert({
          type: "success",
          text: "Generation created successfully",
        }),
      );
      return response.data;
    } catch (e: unknown) {
      if (isAxiosError(e) && e.response?.status === 429) throw e;
      const error = e as { response?: { data?: { detail?: string } } };
      if (error.response?.data) {
        thunkAPI.dispatch(
          displayAlert({
            type: "error",
            text:
              error.response.data.detail || "Failed to create persona group",
          }),
        );
      }
      throw e;
    }
  },
);

export const deletePersonaGroup = createAsyncThunk(
  "personaGroups/deletePersonaGroup",
  async (id: number, thunkAPI) => {
    try {
      await personaGroupsApi.deletePersonaGroup(id);
      thunkAPI.dispatch(
        displayAlert({
          type: "success",
          text: "Persona group deleted successfully",
        }),
      );
      return id;
    } catch (e: unknown) {
      if (isAxiosError(e) && e.response?.status === 429) throw e;
      const error = e as { response?: { data?: { detail?: string } } };
      if (error.response?.data) {
        thunkAPI.dispatch(
          displayAlert({
            type: "error",
            text:
              error.response.data.detail || "Failed to delete persona group",
          }),
        );
      }
      throw e;
    }
  },
);

export const fetchPersonaGroupPersonas = createAsyncThunk(
  "personaGroups/fetchPersonaGroupsPersonas",
  async (id: number, thunkAPI) => {
    try {
      const response = await personaGroupsApi.fetchPersonaGroupPersonas(id);
      return response.data;
    } catch (e: unknown) {
      if (isAxiosError(e) && e.response?.status === 429) throw e;
      const error = e as { response?: { data?: { detail?: string } } };
      if (error.response?.data) {
        thunkAPI.dispatch(
          displayAlert({
            type: "error",
            text:
              error.response.data.detail ||
              "Failed to fetch persona group personas",
          }),
        );
      }
      throw e;
    }
  },
);

export const assignPersonasToPersonaGroup = createAsyncThunk(
  "personaGroups/assignPersonas",
  async (
    { id, body }: { id: number; body: AssignPersonasBodyType },
    thunkAPI,
  ) => {
    try {
      const response = await personaGroupsApi.assignPersonas(id, body);
      thunkAPI.dispatch(
        displayAlert({
          type: "success",
          text: "Personas added to persona group successfully",
        }),
      );
      return response.data;
    } catch (e: unknown) {
      if (isAxiosError(e) && e.response?.status === 429) throw e;
      const error = e as { response?: { data?: { detail?: string } } };
      if (error.response?.data) {
        thunkAPI.dispatch(
          displayAlert({
            type: "error",
            text:
              error.response.data.detail ||
              "Failed to add personas to persona group",
          }),
        );
      }
      throw e;
    }
  },
);

export const removePersonasFromPersonaGroup = createAsyncThunk(
  "personaGroups/removePersonas",
  async (
    { id, body }: { id: number; body: AssignPersonasBodyType },
    thunkAPI,
  ) => {
    try {
      const response = await personaGroupsApi.removePersonas(id, body);
      thunkAPI.dispatch(
        displayAlert({
          type: "success",
          text: "Personas removed from persona group successfully",
        }),
      );
      return response.data;
    } catch (e: unknown) {
      if (isAxiosError(e) && e.response?.status === 429) throw e;
      const error = e as { response?: { data?: { detail?: string } } };
      if (error.response?.data) {
        thunkAPI.dispatch(
          displayAlert({
            type: "error",
            text:
              error.response.data.detail ||
              "Failed to remove personas from persona group",
          }),
        );
      }
      throw e;
    }
  },
);
